import { useState, useCallback } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { useNavigate } from 'react-router-dom'
import { paths } from 'src/routes/paths'
import { Input } from 'src/shared/components/ui/input'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { TableCell, TableRow } from 'src/shared/components/ui/table'
import { QuoteSubItem_, useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { ArrowRight, Trash2 } from 'lucide-react'
import { DesignImage } from 'src/shared/components/Design'
import { DeleteConfirmDialog } from '../../../../shared/DeleteConfirmDialog'
import { Tooltip } from 'src/shared/components/ui'
import { useDebouncedFieldUpdate } from '../../../../../hooks'
import { LineItemNotesField } from '../../../../shared/LineItemNotesPopover'
import { cn } from 'src/shared/components/ui/utils'

export const DecorationRow = ({
  quote_item,
  quote_sub_item,
  isEditMode = false,
  showDiscountColumn = true,
  isReadOnly = false,
}: {
  quote_item: QuoteItem_
  quote_sub_item: QuoteSubItem_
  isEditMode?: boolean
  showDiscountColumn?: boolean
  isReadOnly?: boolean
}) => {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { deleteQuoteSubItem, updateQuoteSubItem } = useQuoteSubItemApi()

  const updateUnitPrice = useCallback(
    (value: number) =>
      updateQuoteSubItem.mutate({ id: quote_sub_item.id, unit_price: value }),
    [quote_sub_item.id, updateQuoteSubItem]
  )
  const updateQuantity = useCallback(
    (value: number) =>
      updateQuoteSubItem.mutate({ id: quote_sub_item.id, quantity: value }),
    [quote_sub_item.id, updateQuoteSubItem]
  )
  const updateDescription = useCallback(
    (value: string) =>
      updateQuoteSubItem.mutate({ id: quote_sub_item.id, description: value }),
    [quote_sub_item.id, updateQuoteSubItem]
  )
  const updateNotes = useCallback(
    (value: string) =>
      updateQuoteSubItem.mutate({
        id: quote_sub_item.id,
        notes: value.trim() === '' ? null : value,
      }),
    [quote_sub_item.id, updateQuoteSubItem]
  )

  const [unitPriceLocal, , handleUnitPriceChange] = useDebouncedFieldUpdate(
    quote_sub_item.unit_price ?? 0,
    updateUnitPrice
  )
  const [quantity, , handleQuantityChange] = useDebouncedFieldUpdate(
    quote_sub_item.quantity ?? 1,
    updateQuantity
  )
  const [descriptionLocal, , handleDescriptionChange] = useDebouncedFieldUpdate(
    quote_sub_item.description ?? '',
    updateDescription
  )
  const [notesLocal, , handleNotesChange] = useDebouncedFieldUpdate(
    quote_sub_item.notes ?? '',
    updateNotes
  )

  const handleDelete = async () => {
    const hasSetupCost = quote_item?.quote_sub_item?.find(
      (qsi) =>
        qsi.type === 'setup_cost' &&
        // @ts-ignore data links setup cost to decoration
        qsi.data?.quote_sub_item_id === quote_sub_item?.id!
    )

    if (hasSetupCost) {
      await deleteQuoteSubItem.mutateAsync(hasSetupCost?.id)
    }

    deleteQuoteSubItem.mutate(quote_sub_item.id)
    setShowDeleteDialog(false)
  }

  return (
    <>
      <TableRow className="border-b border-gray-200 hover:bg-gray-100/80 w-full group">
        <TableCell className="py-2 px-5 pl-12 text-sm text-gray-700 align-middle border-l-2 border-violet-200">
          <Row className="gap-3 items-center">
            <Tooltip
              content={
                <DesignImage
                  logo_url={quote_sub_item.design?.[0]?.design_url || ''}
                  image_url={quote_sub_item.design?.[0]?.image_url || ''}
                  design={quote_sub_item.design?.[0] as any}
                  containerClassName="w-[300px] h-[300px] object-cover"
                />
              }
              side="left"
              delayDuration={300}
            >
              <span className="inline-block">
                <DesignImage
                  logo_url={quote_sub_item.design?.[0]?.design_url || ''}
                  image_url={quote_sub_item.design?.[0]?.image_url || ''}
                  design={quote_sub_item.design?.[0] as any}
                  containerClassName="w-8 h-8 object-cover rounded-sm p-1 border border-gray-200"
                />
              </span>
            </Tooltip>

            <Column className="min-w-0 flex-1 gap-1">
              <span className="inline-flex w-fit items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                {t('Decoration', 'Dekoration')}
              </span>
              {isEditMode ? (
                <Input
                  value={descriptionLocal}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className="h-7 min-w-0 border-gray-300 px-2 text-sm"
                />
              ) : (
                <Row
                  className={cn(
                    'group min-w-0 items-center gap-0.5',
                    isReadOnly ? '' : 'cursor-pointer'
                  )}
                  onClick={() =>
                    !isReadOnly &&
                    navigate(
                      `${paths.requests}/${quote_item.quote_id}/order_items/${quote_item.id}/sub_item/${quote_sub_item.id}`
                    )
                  }
                >
                  <p className="text-sm text-gray-900">
                    {t(quote_sub_item.description!)}
                  </p>

                  {!isReadOnly && (
                    <ArrowRight
                      className="ml-1 w-3.5 translate-x-0 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100"
                      style={{ transitionProperty: 'opacity, transform' }}
                    />
                  )}
                </Row>
              )}
              <LineItemNotesField
                value={notesLocal}
                onChange={handleNotesChange}
                canEdit={!isReadOnly}
                readOnlyNote={quote_sub_item.notes}
              />
            </Column>
          </Row>
        </TableCell>

        {showDiscountColumn && (
          <TableCell className="py-2 px-5 text-sm text-gray-700 align-middle text-center" />
        )}

        <TableCell className="py-2 px-5 text-sm text-gray-700 align-middle text-right">
          <Row className="items-center justify-end gap-3">
            {isEditMode ? (
              <input
                type="number"
                value={quantity}
                onChange={(e) =>
                  handleQuantityChange(Number(e.target.value) || 1)
                }
                className="input-style h-7 w-14 shrink-0 px-1 text-center text-sm tabular-nums"
              />
            ) : (
              <span className="w-14 shrink-0 text-center tabular-nums">
                {quantity}
              </span>
            )}
            {isEditMode ? (
              <Input
                type="number"
                value={unitPriceLocal}
                onChange={(e) =>
                  handleUnitPriceChange(Number(e.target.value) || 0)
                }
                className="h-7 w-20 border-gray-300 px-1 text-right text-sm"
              />
            ) : (
              <span>{(quote_sub_item.unit_price ?? 0) * quantity}</span>
            )}
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
            </button>
          </Row>
        </TableCell>
      </TableRow>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={t('Delete decoration', 'Ta bort dekoration')}
        description={`${t(
          'Are you sure you want to delete this decoration?',
          'Är du säker på att du vill ta bort denna dekoration?'
        )} ${t(
          'This action cannot be undone.',
          'Denna åtgärd kan inte ångras.'
        )}`}
        onConfirm={handleDelete}
      />
    </>
  )
}
